/**
 * NEDISO Room Init — Bootstrapper + GPU Detection + Fallback Routing
 * Sprint 2, Phase 2.1 + Light/Dark Mode Support (v2, 28.03.2026)
 *
 * Flow:
 * 1. Detect GPU tier (0-3)
 * 2. Tier 0 → show fallback HTML + load Pannellum
 * 3. Tier 1-3 → load Three.js + GSAP + room scene
 * 4. Manage loading screen progress
 * 5. Theme-aware: reads html[data-theme], passes to scene
 */
(function () {
  'use strict';

  /* === CONSTANTS === */
  var THREE_VERSION = '0.146.0';
  var CDN = {
    three: 'https://cdn.jsdelivr.net/npm/three@' + THREE_VERSION + '/build/three.min.js',
    gltf: 'https://cdn.jsdelivr.net/npm/three@' + THREE_VERSION + '/examples/js/loaders/GLTFLoader.js',
    draco: 'https://cdn.jsdelivr.net/npm/three@' + THREE_VERSION + '/examples/js/loaders/DRACOLoader.js',
    meshopt: 'https://cdn.jsdelivr.net/npm/three@' + THREE_VERSION + '/examples/js/libs/meshopt_decoder.js',
    orbit: 'https://cdn.jsdelivr.net/npm/three@' + THREE_VERSION + '/examples/js/controls/OrbitControls.js',
    rgbe: 'https://cdn.jsdelivr.net/npm/three@' + THREE_VERSION + '/examples/js/loaders/RGBELoader.js',
    copyShader: 'https://cdn.jsdelivr.net/npm/three@' + THREE_VERSION + '/examples/js/shaders/CopyShader.js',
    luminosity: 'https://cdn.jsdelivr.net/npm/three@' + THREE_VERSION + '/examples/js/shaders/LuminosityHighPassShader.js',
    fxaaShader: 'https://cdn.jsdelivr.net/npm/three@' + THREE_VERSION + '/examples/js/shaders/FXAAShader.js',
    effectComposer: 'https://cdn.jsdelivr.net/npm/three@' + THREE_VERSION + '/examples/js/postprocessing/EffectComposer.js',
    renderPass: 'https://cdn.jsdelivr.net/npm/three@' + THREE_VERSION + '/examples/js/postprocessing/RenderPass.js',
    shaderPass: 'https://cdn.jsdelivr.net/npm/three@' + THREE_VERSION + '/examples/js/postprocessing/ShaderPass.js',
    bloomPass: 'https://cdn.jsdelivr.net/npm/three@' + THREE_VERSION + '/examples/js/postprocessing/UnrealBloomPass.js',
    gsap: 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/gsap.min.js',
    pannellum_js: 'https://cdn.jsdelivr.net/npm/pannellum@2.5.6/build/pannellum.js',
    pannellum_css: 'https://cdn.jsdelivr.net/npm/pannellum@2.5.6/build/pannellum.css'
  };

  /* === THEME HELPER === */
  function getTheme() {
    var dt = document.documentElement.getAttribute('data-theme');
    if (dt === 'light' || dt === 'dark') return dt;
    return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  }

  function isLight() { return getTheme() === 'light'; }

  /* === DOM REFS === */
  var loadingEl = document.getElementById('room-loading');
  var loadFill = document.getElementById('room-load-fill');
  var loadText = document.getElementById('room-load-text');
  var canvasContainer = document.getElementById('room-canvas-container');
  var fallbackEl = document.getElementById('room-fallback');
  var cartBtn = document.getElementById('room-cart-btn');
  var cartCount = document.getElementById('room-cart-count');
  var panelEl = document.getElementById('room-product-panel');
  var panelClose = document.getElementById('room-panel-close');
  var panelContent = document.getElementById('room-qv-content');
  var roomSelector = document.getElementById('room-selector');

  /* === LOADING PROGRESS === */
  var _loadSteps = 0;
  var _loadTotal = 3; // GPU detect + scripts + scene init

  function setProgress(pct, text) {
    if (loadFill) loadFill.style.width = Math.min(100, pct) + '%';
    if (loadText && text) loadText.textContent = text;
  }

  function stepProgress(text) {
    _loadSteps++;
    setProgress((_loadSteps / _loadTotal) * 100, text);
  }

  function hideLoading() {
    if (loadingEl) {
      loadingEl.classList.add('is-done');
      setTimeout(function () {
        loadingEl.style.display = 'none';
      }, 700);
    }
  }

  /* === GPU TIER DETECTION === */
  function detectGPUTier() {
    var canvas = document.createElement('canvas');
    var gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
    if (!gl) return { tier: 0, renderer: 'none' };

    var ext = gl.getExtension('WEBGL_debug_renderer_info');
    var renderer = ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : 'unknown';

    // Tier 1: Integrated GPUs (Intel, Mali, basic Adreno)
    if (/Intel|UHD|HD Graphics|Iris|Mali|Adreno [1-5]|PowerVR|SwiftShader/i.test(renderer)) {
      return { tier: 1, renderer: renderer };
    }
    // Tier 3: High-end GPUs
    if (/RTX|RX [67]|RX 7|Radeon Pro [5-7]|M[2-4]|M1 Pro|M1 Max|M1 Ultra|Apple M[2-4]/i.test(renderer)) {
      return { tier: 3, renderer: renderer };
    }
    // Tier 2: Everything else (mid-range)
    return { tier: 2, renderer: renderer };
  }

  /* === SCRIPT LOADER === */
  function loadScript(url, cb) {
    var s = document.createElement('script');
    s.src = url;
    s.crossOrigin = 'anonymous';
    s.onload = function () { if (cb) cb(null); };
    s.onerror = function () { if (cb) cb(new Error('Failed to load: ' + url)); };
    document.head.appendChild(s);
  }

  function loadCSS(url) {
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    document.head.appendChild(link);
  }

  function loadScriptsParallel(urls, cb) {
    var loaded = 0;
    var errors = [];
    urls.forEach(function (url) {
      loadScript(url, function (err) {
        if (err) errors.push(err);
        loaded++;
        if (loaded === urls.length) cb(errors.length ? errors : null);
      });
    });
  }

  /* === PRODUCT PANEL === */
  function openPanel() {
    panelEl.classList.add('is-open');
    panelEl.setAttribute('aria-hidden', 'false');
  }

  function closePanel() {
    panelEl.classList.remove('is-open');
    panelEl.setAttribute('aria-hidden', 'true');
  }

  if (panelClose) panelClose.addEventListener('click', closePanel);

  /* Close panel on Escape */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      closePanel();
      if (window.nedisoCart) window.nedisoCart.close();
    }
  });

  /* === CART INTEGRATION === */
  function updateCartBadge() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (cartCount) {
          cartCount.textContent = cart.item_count;
          cartCount.style.display = cart.item_count > 0 ? '' : 'none';
        }
      });
  }

  if (cartBtn) {
    cartBtn.addEventListener('click', function () {
      if (window.nedisoCart) {
        window.nedisoCart.refresh();
        window.nedisoCart.open();
      }
    });
  }

  /* === STOREFRONT BRIDGE === */
  var StorefrontBridge = {
    getProduct: function (handle) {
      return fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); });
    },
    getCollectionProducts: function (handle, limit) {
      limit = limit || 12;
      return fetch('/collections/' + handle + '/products.json?limit=' + limit, { credentials: 'same-origin' })
        .then(function (r) { return r.json(); });
    },
    addToCart: function (variantId, quantity, properties) {
      quantity = quantity || 1;
      properties = properties || {};
      return fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: quantity, properties: properties }] })
      }).then(function (r) { return r.json(); })
        .then(function (data) {
          updateCartBadge();
          return data;
        });
    },
    getCart: function () {
      return fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); });
    }
  };

  /* === QUICK-VIEW IN PANEL === */
  function showProductInPanel(handle) {
    var loadColor = isLight() ? 'rgba(0,0,0,.3)' : 'rgba(255,255,255,.3)';
    panelContent.innerHTML = '<div style="text-align:center;padding:60px 20px;color:' + loadColor + '">Laden...</div>';
    openPanel();

    StorefrontBridge.getProduct(handle).then(function (product) {
      var img = product.featured_image ? product.featured_image.replace(/(\.[a-z]+)(\?|$)/, '_600x$1$2') : '';
      var v = product.variants[0];
      var hasVariants = product.variants.length > 1;

      var h = '';
      if (img) h += '<img class="room-qv__img" src="' + img + '" alt="' + product.title + '" loading="eager">';
      h += '<h3 class="room-qv__title">' + product.title + '</h3>';
      h += '<p class="room-qv__price">' + formatMoney(v.price) + ' ' + document.body.getAttribute('data-price-tax-label') + '</p>';

      if (hasVariants) {
        h += '<select class="room-qv__variants" id="room-qv-variant">';
        product.variants.forEach(function (vr) {
          h += '<option value="' + vr.id + '" data-price="' + vr.price + '"';
          if (!vr.available) h += ' disabled';
          h += '>' + vr.title + (vr.available ? '' : ' (ausverkauft)') + '</option>';
        });
        h += '</select>';
      }

      h += '<button class="room-qv__add" id="room-qv-add" data-variant-id="' + v.id + '">In den Warenkorb</button>';
      h += '<div class="room-qv__meta">';
      h += '<a href="/products/' + product.handle + '">Zur Produktseite &rarr;</a>';
      if (product.vendor) h += '<br>Marke: ' + product.vendor;
      h += '</div>';

      panelContent.innerHTML = h;

      /* Bind variant select */
      var sel = document.getElementById('room-qv-variant');
      var addBtn = document.getElementById('room-qv-add');

      if (sel) {
        sel.addEventListener('change', function () {
          addBtn.setAttribute('data-variant-id', sel.value);
          var opt = sel.options[sel.selectedIndex];
          if (opt) {
            var priceEl = panelContent.querySelector('.room-qv__price');
            if (priceEl) priceEl.textContent = formatMoney(parseInt(opt.getAttribute('data-price'), 10)) + ' ' + document.body.getAttribute('data-price-tax-label');
          }
        });
      }

      addBtn.addEventListener('click', function () {
        var vid = parseInt(addBtn.getAttribute('data-variant-id'));
        addBtn.textContent = 'Wird hinzugefuegt...';
        addBtn.disabled = true;
        StorefrontBridge.addToCart(vid, 1).then(function () {
          addBtn.textContent = 'Hinzugefuegt!';
          setTimeout(function () {
            addBtn.textContent = 'In den Warenkorb';
            addBtn.disabled = false;
          }, 1500);
          /* Open cart drawer */
          if (window.nedisoCart) {
            window.nedisoCart.refresh();
            window.nedisoCart.open();
          }
        });
      });
    });
  }

  function formatMoney(cents) {
    var eur = (cents / 100).toFixed(2).replace('.', ',');
    return eur + ' \u20AC';
  }

  /* === ROOM SELECTOR (2 rooms: Konferenz + Showroom) === */
  var _currentRoom = 'konferenz';

  if (roomSelector) {
    roomSelector.addEventListener('click', function (e) {
      var btn = e.target.closest('.room-selector__btn');
      if (!btn) return;
      var newRoom = btn.getAttribute('data-room');
      if (newRoom === _currentRoom) return;

      _currentRoom = newRoom;
      roomSelector.querySelectorAll('.room-selector__btn').forEach(function (b) {
        var isActive = b.getAttribute('data-room') === newRoom;
        b.classList.toggle('is-active', isActive);
        b.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      /* pushState URL */
      var roomUrls = {
        konferenz: '/pages/showroom'
      };
      var url = roomUrls[newRoom] || '/pages/showroom';
      history.pushState({ room: newRoom }, '', url);

      /* Dispatch event for scene to handle */
      document.dispatchEvent(new CustomEvent('nediso:room-change', { detail: { room: newRoom } }));
    });
  }

  /* Handle browser back/forward */
  window.addEventListener('popstate', function (e) {
    if (e.state && e.state.room) {
      _currentRoom = e.state.room;
      roomSelector.querySelectorAll('.room-selector__btn').forEach(function (b) {
        var isActive = b.getAttribute('data-room') === _currentRoom;
        b.classList.toggle('is-active', isActive);
        b.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      document.dispatchEvent(new CustomEvent('nediso:room-change', { detail: { room: _currentRoom } }));
    }
  });

  /* === PANNELLUM 2D FALLBACK === */
  function initFallback() {
    setProgress(50, 'Lade 2D-Ansicht...');
    /* Hide canvas, show fallback HTML */
    canvasContainer.style.display = 'none';
    if (roomSelector) roomSelector.style.display = 'none';
    fallbackEl.style.display = 'block';
    fallbackEl.classList.add('is-active');
    fallbackEl.setAttribute('aria-hidden', 'false');

    /* Load Pannellum for 360 viewer if image available */
    loadCSS(CDN.pannellum_css);
    loadScript(CDN.pannellum_js, function () {
      setProgress(100, 'Fertig');
      hideLoading();
      /* Pannellum init would go here if 360 image exists */
      /* For now, the SEO HTML fallback is sufficient */
    });
  }

  /* === 3D SCENE INIT === */
  function init3DScene(gpuInfo) {
    setProgress(40, 'Lade 3D-Engine...');

    /* Phase 1: Three.js core + GSAP */
    loadScriptsParallel([CDN.three, CDN.gsap], function (errors) {
      if (errors) {
        console.warn('[NEDISO] 3D script load failed, falling back to 2D', errors);
        initFallback();
        return;
      }

      stepProgress('3D-Engine geladen');
      setProgress(50, 'Lade GLTF-Loader...');

      /* Phase 2a: Loaders + shaders (no ordering deps among these) */
      loadScriptsParallel(
        [CDN.gltf, CDN.draco, CDN.meshopt, CDN.orbit, CDN.rgbe, CDN.copyShader, CDN.luminosity, CDN.fxaaShader],
        function () {
          setProgress(65, 'Lade Post-Processing...');

          /* Phase 2b: PostProcessing (must load AFTER CopyShader + LuminosityHighPassShader) */
          loadScriptsParallel(
            [CDN.effectComposer, CDN.renderPass, CDN.shaderPass, CDN.bloomPass],
            function () {
              setProgress(75, 'Baue Raum...');

              /* Expose room object to scene.js */
              window.nedisoRoom = {
                gpu: gpuInfo,
                container: canvasContainer,
                bridge: StorefrontBridge,
                showProduct: showProductInPanel,
                closePanel: closePanel,
                setProgress: setProgress,
                hideLoading: hideLoading,
                formatMoney: formatMoney,
                currentRoom: function () { return _currentRoom; },
                theme: getTheme()
              };

              /* Dispatch — 50ms ensures scene.js (defer) has registered its listener */
              setTimeout(function () {
                document.dispatchEvent(new CustomEvent('nediso:room-ready', {
                  detail: { tier: gpuInfo.tier, renderer: gpuInfo.renderer, theme: getTheme() }
                }));
              }, 50);

              /* Safety: hide loading screen after 10s regardless */
              setTimeout(function () {
                if (!loadingEl.classList.contains('is-done')) {
                  setProgress(100, 'Fertig');
                  hideLoading();
                }
              }, 10000);
            }
          ); /* end phase 2b */
        }
      ); /* end phase 2a */
    }); /* end phase 1 */
  }

  /* === CLASSIC MODE CHECK === */
  function isClassicMode() {
    return window.location.search.indexOf('classic=true') !== -1;
  }

  /* === BOOT === */
  function boot() {
    /* Classic mode → show standard Shopify content */
    if (isClassicMode()) {
      canvasContainer.style.display = 'none';
      if (roomSelector) roomSelector.style.display = 'none';
      document.querySelector('.room-nav').style.display = 'none';
      fallbackEl.style.display = 'block';
      fallbackEl.classList.add('is-active');
      fallbackEl.setAttribute('aria-hidden', 'false');
      hideLoading();
      return;
    }

    setProgress(10, 'Erkenne Hardware...');
    var gpuInfo = detectGPUTier();
    stepProgress('GPU: Tier ' + gpuInfo.tier + ' (' + gpuInfo.renderer.substring(0, 40) + ')');

    if (gpuInfo.tier === 0) {
      initFallback();
    } else {
      _loadTotal = 4;
      init3DScene(gpuInfo);
    }

    /* Init cart badge */
    updateCartBadge();

    /* Set initial history state */
    history.replaceState({ room: 'konferenz' }, '', window.location.pathname);
  }

  /* Wait for DOM if needed */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* === EXPOSE GLOBALLY === */
  window.nedisoRoomInit = {
    bridge: StorefrontBridge,
    showProduct: showProductInPanel,
    closePanel: closePanel,
    updateCartBadge: updateCartBadge,
    currentRoom: function () { return _currentRoom; },
    getTheme: getTheme
  };

})();
